import { useState, useEffect, useMemo } from "react"
import { ref, onValue, remove, update, get } from "../lib/firebase-database"
import { ref as storageRef, deleteObject, listAll } from "../lib/firebase-storage"
import { auth, db, storage } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import { useNavigate } from "react-router-dom"
import { AdminLayout } from "./AdminLayout"
import { Loader2, Edit, Trash2, Eye, EyeOff, Search, Plus } from "lucide-react"

export default function HotelsPage() {
  const [villas, setVillas] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    setIsLoading(true)
    setError(null)
    const villasRef = ref(db, "villas")
    onValue(
      villasRef,
      (snapshot) => {
        const data = snapshot.val()
        if (data) {
          const villaList = Object.entries(data).map(([id, villa]) => ({ id, ...villa }))
          setVillas(villaList)
        } else {
          setVillas([])
        }
        setIsLoading(false)
      },
      (err) => {
        console.error("Error fetching villas:", err)
        setError(`Failed to load villas: ${err.message}`)
        setIsLoading(false)
      }
    )
  }, [])

  const handleDeleteVilla = async (id) => {
    if (!window.confirm("Delete this villa? This cannot be undone.")) return
    try {
      await remove(ref(db, `villas/${id}`))
      try {
        const imagesRef = storageRef(storage, `villas/${id}`)
        const imagesList = await listAll(imagesRef)
        await Promise.all(imagesList.items.map(deleteObject))
      } catch (_) {}
      try { await deleteObject(storageRef(storage, `thumbnails/${id}`)) } catch (_) {}
      try { await deleteObject(storageRef(storage, `location-maps/${id}`)) } catch (_) {}
      try { await deleteObject(storageRef(storage, `owners/${id}`)) } catch (_) {}
      try {
        const managementRef = storageRef(storage, `management/${id}`)
        const managementList = await listAll(managementRef)
        await Promise.all(managementList.items.map(deleteObject))
      } catch (_) {}
      try { await remove(ref(db, `reviews/${id}`)) } catch (_) {}
      try {
        const wishlistsRef = ref(db, "wishlists")
        const wishlistsSnapshot = await get(wishlistsRef)
        const wishlists = wishlistsSnapshot.val()
        if (wishlists) {
          await Promise.all(
            Object.entries(wishlists).map(([userId, userWishlist]) =>
              userWishlist && userWishlist[id]
                ? remove(ref(db, `wishlists/${userId}/${id}`))
                : Promise.resolve()
            )
          )
        }
      } catch (_) {}
      try {
        const chatsRef = ref(db, "chats")
        const chatsSnapshot = await get(chatsRef)
        const chats = chatsSnapshot.val()
        if (chats) {
          await Promise.all(
            Object.entries(chats).map(([chatId, chat]) =>
              chat.villaId === id ? remove(ref(db, `chats/${chatId}`)) : Promise.resolve()
            )
          )
        }
      } catch (_) {}
      setVillas(villas.filter((villa) => villa.id !== id))
    } catch (err) {
      console.error("Error deleting villa:", err)
      setError(`Failed to delete villa: ${err.message}`)
    }
  }

  const toggleVillaVisibility = async (id, currentVisibility) => {
    const action = currentVisibility ? "show" : "hide"
    if (!window.confirm(`${action[0].toUpperCase() + action.slice(1)} this villa?`)) return
    try {
      const newVisibility = !currentVisibility
      await update(ref(db, `villas/${id}`), { isHidden: newVisibility })
      setVillas(villas.map((v) => (v.id === id ? { ...v, isHidden: newVisibility } : v)))
    } catch (err) {
      console.error("Error updating villa visibility:", err)
      setError(`Failed to update villa visibility: ${err.message}`)
    }
  }

  const filteredVillas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return villas
    return villas.filter((v) =>
      v.name?.toLowerCase().includes(q) ||
      v.address?.toLowerCase().includes(q) ||
      v.owner?.name?.toLowerCase().includes(q)
    )
  }, [villas, searchQuery])

  const handleLogout = () => {
    auth.signOut().then(() => navigate("/login"))
  }

  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Manage Villas</h1>
            <p className="text-sm text-gray-500 mt-1">
              {villas.length} {villas.length === 1 ? "villa" : "villas"} listed.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/add-property")}
            className="inline-flex items-center justify-center px-4 py-2 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-900"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Villa
          </button>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, address or owner"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading villas...
          </div>
        ) : filteredVillas.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg py-16 text-center text-sm text-gray-500">
            {searchQuery ? `No villas match "${searchQuery}"` : "No villas yet."}
          </div>
        ) : (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {filteredVillas.map((villa) => (
              <div
                key={villa.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col"
              >
                <div className="relative w-full h-44 bg-gray-100">
                  <img
                    src={villa.images?.[0] || villa.thumbnail || "/placeholder.svg"}
                    alt={villa.name}
                    className="w-full h-full object-cover"
                  />
                  {villa.isHidden && (
                    <span className="absolute top-2 left-2 text-[11px] font-medium px-1.5 py-0.5 rounded bg-black/80 text-white">
                      Hidden
                    </span>
                  )}
                </div>
                <div className="p-4 flex flex-col gap-3 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">
                      {villa.name}
                    </h3>
                    <p className="text-sm font-semibold text-gray-900 shrink-0">
                      ₹{villa.rate}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2">{villa.address}</p>
                  {villa.owner?.name && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <img
                        src={villa.owner?.image || "/placeholder.svg"}
                        alt={villa.owner?.name}
                        className="w-6 h-6 rounded-full object-cover bg-gray-100"
                      />
                      <span className="truncate">{villa.owner?.name}</span>
                    </div>
                  )}
                  <div className="flex gap-2 mt-auto pt-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/edit-property/${villa.id}`)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      <Edit className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleVillaVisibility(villa.id, villa.isHidden)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      {villa.isHidden ? (
                        <>
                          <Eye className="w-3.5 h-3.5" /> Show
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3.5 h-3.5" /> Hide
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteVilla(villa.id)}
                      className="inline-flex items-center justify-center px-2 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                      aria-label="Delete villa"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
